import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import CartDrawer from '../components/common/CartDrawer';
import api from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function AboutPage() {
  const [about,      setAbout]      = useState(null);
  const [leadership, setLeadership] = useState([]);
  const [contact,    setContact]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/content/about').catch(() => ({ data: { data: null } })),
      api.get('/content/leadership').catch(() => ({ data: { data: [] } })),
      api.get('/content/contact').catch(() => ({ data: { data: null } })),
      api.get('/certificates').catch(() => ({ data: { data: [] } })),
    ]).then(([a, l, c, cert]) => {
      setAbout(a.data.data);
      setLeadership(Array.isArray(l.data.data) ? l.data.data : []);
      setContact(c.data.data);
      setCertificates(Array.isArray(cert.data.data) ? cert.data.data : []);
    }).finally(() => setLoading(false));
  }, []);

  const defaultAbout = {
    title:   'About Central Fasteners',
    history: 'Central Fasteners has been a trusted supplier of industrial fasteners for over a decade. We specialize in high-quality bolts, nuts, screws, and washers for manufacturing, construction, and engineering industries across India.',
    vision:  'To be India\'s most reliable industrial fastener supplier, committed to quality, precision, and customer satisfaction.',
    mission: 'To deliver precision-engineered fasteners with exceptional service, ensuring every client gets the exact product they need.',
  };

  const displayAbout  = about  || defaultAbout;
  const displayLeaders = leadership;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />

      <main className="flex-1 pt-20">
        {/* Hero */}
        <div className="bg-on-surface text-white px-6 md:px-10 py-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-2 h-full bg-primary-container" />
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 font-label text-xs text-gray-500 uppercase tracking-widest mb-4">
              <Link to="/" className="hover:text-gray-300">Home</Link>
              <span>/</span>
              <span className="text-gray-400">About Us</span>
            </div>
            <h1 className="font-headline text-5xl md:text-7xl uppercase tracking-tight text-white">
              About Us
            </h1>
            <p className="font-body text-gray-400 mt-4 max-w-2xl text-lg leading-relaxed">
              {displayAbout.tagline || 'Industrial precision since day one.'}
            </p>
          </div>
        </div>

        {/* History section */}
        <section className="py-20 px-6 md:px-10">
          <div className="max-w-screen-2xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <div className="accent-bar" />
                <h3 className="font-Lato font-black text-2xl uppercase mb-6">Our Story</h3>
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-4 skeleton" />)}
                  </div>
                ) : (
                  <div className="font-body text-secondary text-base leading-relaxed space-y-4">
                    {(displayAbout.history || '').split('\n').filter(Boolean).map((para, i) => (
                      <p key={i}>{para}</p>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-6">
                {/* Vision */}
                <div className="bg-on-surface text-white p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-primary-container" />
                  <div className="pl-4">
                    <span className="material-symbols-outlined text-primary-container text-[32px] mb-3 block">visibility</span>
                    <h3 className="font-Lato font-black text-2xl uppercase  text-white mb-3">Our Vision</h3>
                    <p className="font-body text-gray-400 leading-relaxed text-sm">{displayAbout.vision || ''}</p>
                  </div>
                </div>

                {/* Mission */}
                <div className="bg-surface-container-low border border-outline-variant/20 p-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-primary-container" />
                  <div className="pl-4">
                    <span className="material-symbols-outlined text-primary-container text-[32px] mb-3 block">flag</span>
                    <h3 className="font-Lato font-black text-2xl uppercase  text-on-surface mb-3">Our Mission</h3>
                    <p className="font-body text-secondary leading-relaxed text-sm">{displayAbout.mission || ''}</p>
                  </div>
                </div>

                {/* Values */}
                {displayAbout.values && (
                  <div className="bg-primary-container p-8">
                    <span className="material-symbols-outlined text-white text-[32px] mb-3 block">diamond</span>
                    <h3 className="font-sans font-black text-2xl uppercase tracking-tight text-white mb-3">Our Values</h3>
                    <p className="font-body text-white/80 leading-relaxed text-sm">{displayAbout.values}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Leadership section */}
        {displayLeaders.length > 0 && (
          <section className="py-20 px-6 md:px-10 bg-surface-container-low">
            <div className="max-w-screen-2xl mx-auto">
              <div className="text-center mb-14">
                <div className="accent-bar mx-auto" />
                <h2 className="font-sans font-black text-2xl uppercase mb-3">Our Leadership</h2>
                <p className="font-body text-secondary mt-3">The people who guide Central Fasteners forward.</p>
              </div>

              <div className="space-y-12">
  {displayLeaders
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    .map((leader, i) => (
      <div key={i} className="bg-white border border-outline-variant/20 overflow-hidden card-industrial flex flex-col md:flex-row">
        {/* Photo */}
        <div className="w-full md:w-80 flex-shrink-0 bg-surface-container">
          {leader.image
            ? <img src={leader.image}
                alt={leader.name}
                className="w-full h-full object-cover object-top min-h-[320px]" />
            : <div className="w-full min-h-[320px] flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                <span className="material-symbols-outlined text-[80px] text-outline-variant">person</span>
              </div>
          }
        </div>
        {/* Text */}
        <div className="flex-1 p-8 border-l-4 border-primary-container flex flex-col justify-center">
          <p className="font-label text-xs text-primary-container uppercase tracking-widest font-bold mb-2">{leader.title}</p>
          <h3 className="font-Lato font-black text-3xl uppercase text-on-surface mb-4">{leader.name}</h3>
          {leader.description && (
            <p className="font-body text-base text-secondary leading-relaxed">{leader.description}</p>
          )}
        </div>
      </div>
    ))}
</div>
            </div>
          </section>
        )}

        {/* Certificates & Achievements section */}
        {certificates.length > 0 && (
          <section className="py-20 px-6 md:px-10">
            <div className="max-w-screen-2xl mx-auto">
              <div className="text-center mb-14">
                <div className="accent-bar mx-auto" />
                <h2 className="font-sans text-2xl uppercase mb-3">Certificates & Achievements</h2>
                <p className="font-body text-secondary mt-3">Our credentials and industry recognitions.</p>
              </div>
 
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {certificates
                  .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                  .map((cert) => (
                    <div key={cert._id} className="bg-white border border-outline-variant/20 p-4 flex flex-col items-center gap-3 card-industrial text-center">
                      <div className="w-full aspect-square flex items-center justify-center overflow-hidden">
                        <img
                          src={cert.image}
                          alt={cert.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <p className="font-body text-sm font-semibold text-on-surface leading-tight">{cert.title}</p>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        )}

        {/* Contact strip */}
        <section className="bg-on-surface py-16 px-6 md:px-10">
          <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-headline font-black text-3xl text-white uppercase tracking-tight">
                Ready to work with us?
              </h2>
              <p className="font-body text-gray-400 mt-2">
                {contact?.email || ''} · {contact?.phone || ''}
              </p>
            </div>
            <div className="flex gap-4">
              <Link to="/products"  className="btn-primary whitespace-nowrap">Browse Products</Link>
              <Link to="/feedback"  className="btn-secondary border-gray-600 text-gray-300 hover:bg-white hover:text-on-surface whitespace-nowrap">Send Message</Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
